import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  Box,
  Chip,
  Divider,
  Grid,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import CheckIcon from '@mui/icons-material/Check';

import { IQuestion } from '../../../../store/QuestionSlice';
import { ISubmissionQuestion } from '../../../../store/SubmissionSlice';
import {
  getQuestionDifficultyChipColor,
  msToMinutesSecondsNew,
} from '../../../../helper';
import { reportResources } from '../../../../store/ResourcesSlice';
import { getAuthState } from '../../../../store/AuthSlice';
import { useAppSelector } from '../../../../hooks';
import { TP_COLORS } from '../../../../constants';
import ReportModal from '../../../../components/Modals/ReportModal';
import MarkdownRenderer from '../../../../helper/MarkdownRenderer/MarkdownRenderer';

type TQuestionDetailViewerProps = {
  questionData: IQuestion;
  selectedQuestion: ISubmissionQuestion;
};

const labelStyles: React.CSSProperties = {
  backgroundColor: '#E2E8F0',
  color: '#1E293B',
  fontSize: '14px',
  fontWeight: 600,
  borderRadius: '5px',
};

const getAnswerStatusIcon = (question: ISubmissionQuestion) => {
  const chipStyles: SxProps = {
    color: TP_COLORS.white,
    fontSize: '14px',
    fontWeight: 600,
    borderRadius: '5px',
  };
  const iconStyles = { width: '16px', height: '16px', color: TP_COLORS.white };

  if (!question.answer) {
    return (
      <Chip
        label="Skipped"
        sx={{
          ...chipStyles,
          color: TP_COLORS.gray.darken,
        }}
      />
    );
  }

  if (question.answer?.isCorrect) {
    return (
      <Chip
        icon={<CheckIcon style={iconStyles} />}
        label="Correct"
        sx={{
          ...chipStyles,
          backgroundColor: TP_COLORS.green.light,
        }}
      />
    );
  }

  return (
    <Chip
      icon={<ClearIcon style={iconStyles} />}
      label="Incorrect"
      sx={{
        ...chipStyles,
        backgroundColor: TP_COLORS.orange.light,
      }}
    />
  );
};

const QuestionDetailViewer: React.FC<TQuestionDetailViewerProps> = ({
  questionData,
  selectedQuestion,
}) => {
  const params = useParams();
  const dispatch = useDispatch();

  // Store
  const { user: authUser } = useAppSelector(getAuthState);

  // State
  const [state, setState] = useState({
    isReportQuestionModalOpen: false,
  });

  const isOpenEndedQuestion = Boolean(
    selectedQuestion?.isOpenEnded && questionData?.theoryQuestion?.question
  );

  const questionMarks = questionData?.marks?.toString().padStart(2, '0') || '';

  const correctAnswer =
    questionData.answers.find((item) => item.isCorrect)?.content || '';

  const userResponse = isOpenEndedQuestion
    ? selectedQuestion.openEnded?.answer || ''
    : selectedQuestion.answer?.content || '';

  const toggleReportQuestionModal = () => {
    setState((prevState) => ({
      ...prevState,
      isReportQuestionModalOpen: !prevState.isReportQuestionModalOpen,
    }));
  };

  const reportQuestion = (reason: string) => {
    if (params.subtopicId && authUser && authUser._id) {
      dispatch(
        reportResources({
          questionId: questionData._id,
          subTopicId: params.subtopicId,
          userId: authUser._id,
          reason,
        })
      );
    }

    toggleReportQuestionModal();
  };

  return (
    <>
      <Box>
        <Stack
          gap="10px"
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          style={{ marginBottom: '15px' }}
        >
          {!isOpenEndedQuestion ? (
            getAnswerStatusIcon(selectedQuestion)
          ) : (
            <div />
          )}
          <Box display="flex" gap="16px">
            <Chip
              size="small"
              label={questionData.difficulty}
              color={getQuestionDifficultyChipColor(questionData.difficulty)}
              style={{
                borderRadius: labelStyles.borderRadius,
              }}
            />
            {selectedQuestion.status === 'skipped' ? (
              <Chip size="small" label="Skipped" style={labelStyles} />
            ) : null}

            <Box display="flex" gap="8px">
              <Typography>
                <span style={{ fontWeight: '600' }}>
                  {msToMinutesSecondsNew(
                    (selectedQuestion.timerEnd || 0) -
                      (selectedQuestion.timerStart || 0)
                  )}
                </span>{' '}
                time
              </Typography>
              <Divider orientation="vertical" flexItem />
              <Typography>
                <span style={{ fontWeight: '600' }}>{questionMarks}</span>{' '}
                Mark(s)
              </Typography>
            </Box>
          </Box>
        </Stack>
        <div>
          {isOpenEndedQuestion ? (
            <MarkdownRenderer
              content={questionData?.theoryQuestion?.question || ''}
            />
          ) : (
            <div
              className="view ql-editor ql-viewer"
              style={{ marginBottom: '20px' }}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: questionData.details || '',
              }}
            />
          )}
        </div>

        <div style={{ marginBottom: '15px' }}>
          <Grid container spacing={4}>
            <Grid item>
              <Typography color="#94A3B8">Response</Typography>

              <div
                className="view ql-editor ql-viewer"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: userResponse || 'No answer submitted',
                }}
              />
            </Grid>

            {!isOpenEndedQuestion && (
              <Grid item>
                <Typography color={TP_COLORS.green.light}>
                  Correct Answer
                </Typography>

                <div
                  className="view ql-editor ql-viewer"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: correctAnswer }}
                />
              </Grid>
            )}
          </Grid>
        </div>
      </Box>
      {state.isReportQuestionModalOpen && (
        <ReportModal
          open={state.isReportQuestionModalOpen}
          type="question"
          onClose={toggleReportQuestionModal}
          onConfirm={reportQuestion}
        />
      )}
    </>
  );
};

export default QuestionDetailViewer;
